import React, { useEffect, useState, useRef } from 'react';
import { getShipments, getUsers, getFlights, deleteShipment, updateLocation, unassignFlight, countCansShipment, updateCanCount, recountAll, getRecountStatus, cancelRecount, getShipmentImages } from '../../api';
import InsideImagesModal from '../../components/InsideImagesModal';
import CanCountCell from '../../components/CanCountCell';
import ShipmentDrawer from '../../components/ShipmentDrawer';
import ImageViewer from '../../components/ImageViewer';

// Generic searchable combobox: type to filter, click to select
function SearchableSelect({ options, value, onChange, placeholder, displayOf, searchOf, minWidth = 180 }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Sync display when value changes externally (e.g. reset filters).
  // displayOf/options are new refs each render — use ref to avoid re-firing the effect while typing.
  const displayOfRef = useRef(displayOf);
  const optionsRef = useRef(options);
  displayOfRef.current = displayOf;
  optionsRef.current = options;
  useEffect(() => {
    if (!value) { setQuery(''); return; }
    const selected = optionsRef.current.find(o => String(o.value) === String(value));
    if (selected) setQuery(displayOfRef.current(selected));
  }, [value]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = query
    ? options.filter(o => searchOf(o).toLowerCase().includes(query.toLowerCase()))
    : options.slice(0, 12);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input className="fs"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange(''); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          style={{ paddingRight: value ? 28 : 12, minWidth }}
        />
        {value && (
          <button onMouseDown={() => { setQuery(''); setOpen(false); onChange(''); }}
            style={{ position: 'absolute', right: 8, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
        )}
      </div>
      {open && filtered.length > 0 && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, background: 'var(--bg-secondary)', border: '1px solid rgba(15,23,42,0.10)', borderRadius: 8, zIndex: 999, maxHeight: 220, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.5)', minWidth: minWidth + 20 }}>
          {filtered.map(o => (
            <div key={o.value}
              onMouseDown={() => { setQuery(displayOf(o)); setOpen(false); onChange(o.value); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid rgba(15,23,42,0.05)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ShipmentsAssigned() {
  const [shipments, setShipments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [flights, setFlights] = useState([]);
  const [filters, setFilters] = useState({ flight_id:'', customer_id:'', date_from:'', date_to:'', warehouse:'', search:'' });
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [imageModal, setImageModal] = useState(null);
  const [insideModal, setInsideModal] = useState(null);
  const [drawerId, setDrawerId] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [listKey, setListKey] = useState(0);
  // Edit shipment info modal (tracking / order / warehouse)
  const [editModal, setEditModal] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  const openLabelViewer = (url, tracking) => setViewer({ images: [url], title: tracking ? `Label · ${tracking}` : 'Label' });
  const openInsideViewer = async (id, tracking) => {
    try {
      const imgs = await getShipmentImages(id);
      if (!imgs.length) { alert('No inside photos'); return; }
      setViewer({ images: imgs.map(i => i.image_url), title: `Inside · ${tracking}` });
    } catch (e) { alert(e.message); }
  };
  const [countingIds, setCountingIds] = useState(new Set());
  const [recountActive, setRecountActive] = useState(false);
  const [recountStatus, setRecountStatus] = useState(null);
  const [canCountExact, setCanCountExact] = useState('');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  // On mount: if a background job is running, restore the banner
  useEffect(() => {
    getRecountStatus().then(st => {
      if (st && !st.done && st.total > 0) {
        setRecountStatus(st);
        setRecountActive(true);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!recountActive) return;
    let stopped = false;
    const poll = async () => {
      try {
        const st = await getRecountStatus();
        if (stopped) return;
        setRecountStatus(st);
        if (st.done) {
          setRecountActive(false);
          load();
        }
      } catch(e) { console.error(e); }
    };
    poll();
    const iv = setInterval(poll, 2000);
    return () => { stopped = true; clearInterval(iv); };
  }, [recountActive]);

  const load = async () => {
    setLoading(true);
    setSelected([]);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([,v])=>v));
      // Only get shipments with flight
      const all = await getShipments(params);
      setShipments(all.filter(s => s.flight_id));
      setListKey(k => k + 1);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    getUsers({role:'customer'}).then(setCustomers).catch(console.error);
    getFlights().then(setFlights).catch(console.error);
  }, []);

  // Auto-apply: filter change → reload (also runs first time, state is empty)
  useEffect(() => {
    const t = setTimeout(() => load(), 150);
    return () => clearTimeout(t);
  }, [filters.warehouse, filters.flight_id, filters.customer_id, filters.date_from, filters.date_to, filters.search]);

  // Tracking/order search: 300ms debounce + apply only when ≥3 chars (or empty to clear)
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters(f => {
        const next = searchInput.length >= 3 ? searchInput : '';
        return f.search === next ? f : { ...f, search: next };
      });
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleUnassign = async (id) => {
    if (!window.confirm('Unassign flight from this shipment?')) return;
    await unassignFlight([id]);
    load();
  };

  const handleBulkUnassign = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Unassign flight from ${selected.length} selected shipments?`)) return;
    try {
      await unassignFlight(selected);
      setSelected([]);
      load();
    } catch (e) {
      alert(e.message || 'Error: failed to unassign flight');
    }
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x=>x!==id) : [...s,id]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this shipment?')) return;
    await deleteShipment(id); load();
  };

  const handleLocation = async (id, location) => {
    await updateLocation(id, location);
    load();
  };

  const handleSaveEdit = async () => {
    if (!editModal) return;
    setEditLoading(true);
    try {
      await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/shipments/${editModal.id}/edit`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('kdx_token')}` },
        body: JSON.stringify({ tracking_code: editModal.tracking_code, customer_order: editModal.customer_order, warehouse: editModal.warehouse })
      });
      setEditModal(null);
      load();
    } catch(e) { alert(e.message); }
    finally { setEditLoading(false); }
  };

  const handleCount = async (id) => {
    setCountingIds(s => new Set([...s, id]));
    try {
      await countCansShipment(id);
      load();
    } catch(e) { alert('Count error: ' + e.message); }
    finally {
      setCountingIds(s => { const n = new Set(s); n.delete(id); return n; });
    }
  };

  const handleUpdateCount = async (id, val) => {
    const n = val === '' || val === null ? null : parseInt(val, 10);
    if (n !== null && (Number.isNaN(n) || n < 0)) { alert('Quantity must be a number ≥ 0'); return; }
    try {
      await updateCanCount(id, n);
      load();
    } catch(e) { alert(e.message); }
  };

  const distinctCanCounts = [...new Set(shipments.filter(s => s.can_count != null).map(s => s.can_count))].sort((a,b)=>a-b);
  const matchCanCountFilter = (sh) => {
    if (canCountExact === '') return true;
    return sh.can_count === parseInt(canCountExact, 10);
  };
  const filteredShipments = shipments.filter(matchCanCountFilter);
  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredShipments.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visibleShipments = filteredShipments.slice((safePage-1)*PAGE_SIZE, safePage*PAGE_SIZE);
  // Reset to page 1 on filter change
  useEffect(() => { setPage(1); }, [filters, canCountExact, shipments.length]);

  // Select-all behavior — operates on the VISIBLE page; banner suggests expanding to the whole filter
  const visibleIds = visibleShipments.map(s => s.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selected.includes(id));
  const someVisibleSelected = visibleIds.some(id => selected.includes(id));
  const allFilteredSelected = filteredShipments.length > 0 && selected.length >= filteredShipments.length && filteredShipments.every(s => selected.includes(s.id));
  const hasMoreToSelect = filteredShipments.length > visibleShipments.length;

  const toggleAll = () => {
    if (allVisibleSelected) {
      setSelected(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelected(prev => [...new Set([...prev, ...visibleIds])]);
    }
  };
  const selectAllFiltered = () => setSelected(filteredShipments.map(s => s.id));
  const clearAllSelection = () => setSelected([]);

  const totalCans = filteredShipments.reduce((sum,s)=>sum+(s.can_count||0),0);
  const eligibleForCount = filteredShipments.filter(s => s.has_inside_image && (s.can_count == null || s.can_count_status === 'error'));
  const flightColor = (status) => status === 'flown' ? 'var(--accent)' : 'var(--color-warning)';
  const flightBadge = (status) => status === 'flown'
    ? { color: 'var(--accent)', background: 'rgba(79,140,255,0.10)', borderColor: 'rgba(79,140,255,0.22)' }
    : { color: 'var(--color-warning)', background: 'rgba(217,119,6,0.10)', borderColor: 'rgba(217,119,6,0.22)' };

  const handleCountFiltered = async () => {
    if (!eligibleForCount.length) return;
    if (!window.confirm(`AI count for ${eligibleForCount.length} filtered shipments?`)) return;
    try {
      await recountAll({ shipment_ids: eligibleForCount.map(s=>s.id) });
      setRecountActive(true);
    } catch(e) {
      if (/đang chạy/i.test(e.message)) alert('Another count job is running — wait and retry');
      else alert(e.message);
    }
  };

  const handleCancelRecount = async () => {
    try { await cancelRecount(); }
    catch(e) { alert(e.message); }
  };

  return (
    <div>
      <style>{`
        .fs,.fi{background:var(--bg-tertiary);border:1px solid var(--border);border-radius:var(--radius-sm);padding:8px 12px;font-size:12.5px;color:var(--text-primary);font-family:var(--font-sans);transition:border-color var(--transition-fast);outline:none;}
        .fs:focus,.fi:focus{border-color:var(--accent);box-shadow:0 0 0 3px var(--accent-bg);}
        .fb{background:var(--bg-tertiary);color:var(--text-primary);border:1px solid var(--accent-strong);border-radius:8px;padding:8px 18px;font-size:12.5px;font-weight:600;cursor:pointer;font-family:var(--font-sans);transition:all 0.18s;}
        .fb:hover{background:var(--bg-elevated);border-color:var(--accent);}
        .ship-row:hover td{background:rgba(15,23,42,0.025);}
        .del-btn{background:transparent;border:1px solid rgba(220,38,38,0.20);color:#b91c1c;border-radius:var(--radius-sm);padding:4px 9px;font-size:11px;cursor:pointer;transition:all var(--transition-fast);}
        .del-btn:hover{background:rgba(220,38,38,0.08);border-color:rgba(220,38,38,0.35);}
        .cb{width:14px;height:14px;cursor:pointer;accent-color:var(--accent);}
        .modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.7);display:flex;align-items:center;justify-content:center;z-index:100;}
      `}</style>

      <div style={s.topbar}>
        <div>
          <div className="page-eyebrow">MANAGEMENT</div>
          <h1 className="page-title">Assigned Shipments</h1>
          <p className="page-subtitle">Shipments with assigned flights — unassign, edit location, AI counting</p>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
          {totalCans > 0 && (
            <div style={s.canBadge}>
              <span style={s.canLabel}>TOTAL COUNT</span>
              <span style={s.canVal}>{totalCans}</span>
            </div>
          )}
          {eligibleForCount.length > 0 && !recountActive && (
            <button onClick={handleCountFiltered}
              style={{background:'rgba(124,58,237,0.12)',border:'1px solid rgba(124,58,237,0.35)',color:'var(--color-purple)',borderRadius:8,padding:'6px 12px',fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap',}}>
              🤖 AI Count ({eligibleForCount.length})
            </button>
          )}
          <div style={s.badge}>{filteredShipments.length} shipments</div>
        </div>
      </div>

      {recountActive && recountStatus && !recountStatus.done && (
        <div style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',marginBottom:14,background:'rgba(124,58,237,0.06)',border:'1px solid rgba(124,58,237,0.22)',borderRadius:8}}>
          <div style={{flex:1}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:5}}>
              <span style={{fontSize:12,fontWeight:600,color:'var(--color-purple)'}}>🤖 AI counting: {recountStatus.processed}/{recountStatus.total}</span>
              <span style={{fontSize:11,color:'var(--text-muted)'}}>
                ✅ {recountStatus.success} · ⚠ {recountStatus.errors} · ETA ~{Math.max(1, Math.ceil((recountStatus.total - recountStatus.processed) * (recountStatus.throttleMs+5000) / 1000 / 60))} min
              </span>
            </div>
            <div style={{height:5,background:'var(--border)',borderRadius:3,overflow:'hidden'}}>
              <div style={{height:'100%',width:`${recountStatus.total>0?(recountStatus.processed/recountStatus.total*100):0}%`,background:'var(--color-purple)',transition:'width 0.5s'}} />
            </div>
          </div>
          <button onClick={handleCancelRecount}
            style={{background:'rgba(220,38,38,0.10)',color:'var(--color-danger)',border:'1px solid rgba(220,38,38,0.25)',borderRadius:6,padding:'5px 11px',fontSize:11,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}>
            ⏹ Stop
          </button>
        </div>
      )}

      {/* Filters — auto-apply on change, no Filter button */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:16,alignItems:'center'}}>
        {/* Group 1: dropdown applies on select */}
        <select className="fs" value={filters.warehouse} onChange={e=>setFilters(f=>({...f,warehouse:e.target.value}))}>
          <option value="">Warehouse</option>
          <option value="Kho 1">Kho 1</option>
          <option value="Kho 2">Kho 2</option>
          <option value="Kho 3">Kho 3</option>
          <option value="Kho 4">Kho 4</option>
          <option value="Kho Surrey">Kho Surrey</option>
          <option value="ARBOURRIDGE">ARBOURRIDGE</option>
        </select>
        <select className="fs" value={canCountExact} onChange={e=>setCanCountExact(e.target.value)}>
          <option value="">Count</option>
          {distinctCanCounts.map(n => <option key={n} value={n}>{n}</option>)}
        </select>

        {/* Tracking / customer order search — debounced, applies at >= 3 chars */}
        <input
          className="fi"
          style={{minWidth:240}}
          placeholder="Type 3+ chars or full code to search tracking/order..."
          value={searchInput}
          onChange={e=>setSearchInput(e.target.value)}
        />

        {/* Group 3: search combobox — type or pick */}
        <SearchableSelect
          options={flights.map(f => ({
            value: f.id,
            label: <span><span style={{fontVariantNumeric:'tabular-nums',fontWeight:700,color:'var(--accent)',fontSize:12}}>{f.name}</span> <span style={{color:'var(--text-secondary)',fontSize:11}}>· {f.status==='flown'?'Flown':'Pending'}</span></span>,
            search: `${f.name} ${f.status==='flown'?'flown':'pending'}`,
            display: f.name,
          }))}
          value={filters.flight_id}
          onChange={v => setFilters(f => ({ ...f, flight_id: v }))}
          placeholder="Flight code..."
          displayOf={o => o.display}
          searchOf={o => o.search}
          minWidth={190}
        />
        <SearchableSelect
          options={customers.map(c => ({
            value: c.id,
            label: <span><span style={{fontVariantNumeric:'tabular-nums',fontWeight:700,color:'var(--accent)',fontSize:12,marginRight:8}}>{c.code}</span><span style={{color:'var(--text-secondary)'}}>{c.name}</span></span>,
            search: `${c.code} ${c.name}`,
            display: `${c.code} — ${c.name}`,
          }))}
          value={filters.customer_id}
          onChange={v => setFilters(f => ({ ...f, customer_id: v }))}
          placeholder="Customer code..."
          displayOf={o => o.display}
          searchOf={o => o.search}
          minWidth={220}
        />

        {/* Group 4: date */}
        <input className="fi" type="date" value={filters.date_from} onChange={e=>setFilters(f=>({...f,date_from:e.target.value}))} />
        <input className="fi" type="date" value={filters.date_to} onChange={e=>setFilters(f=>({...f,date_to:e.target.value}))} />
      </div>

      {/* Bulk unassign bar */}
      {selected.length > 0 && (
        <div style={{padding:'12px 14px',background:'rgba(217,119,6,0.08)',border:'1px solid rgba(217,119,6,0.22)',borderRadius:'var(--radius-md)',marginBottom:12,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
          <span style={{fontSize:13,color:'var(--color-warning)',fontWeight:700,fontVariantNumeric:'tabular-nums'}}>{selected.length} shipments selected</span>
          {/* Banner: expand selection to all filtered */}
          {allVisibleSelected && hasMoreToSelect && !allFilteredSelected && (
            <button onClick={selectAllFiltered}
              style={{background:'transparent',border:'1px dashed var(--accent)',color:'var(--accent)',borderRadius:'var(--radius-sm)',padding:'4px 10px',fontSize:11,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}>
              ↗ Select all {filteredShipments.length} filtered shipments
            </button>
          )}
          {allFilteredSelected && (
            <span style={{fontSize:11,color:'var(--text-muted)',fontStyle:'italic'}}>
              (all filtered results across pages are selected)
            </span>
          )}
          <button className="btn btn-secondary btn-sm" onClick={handleBulkUnassign}>
            <span className="material-symbols-outlined" style={{fontSize:14,color:'var(--color-warning)'}}>flight</span>
            Unassign ({selected.length})
          </button>
          <button onClick={clearAllSelection} style={{background:'transparent',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:12,marginLeft:'auto'}}>× Deselect</button>
        </div>
      )}

      {/* Table */}
      <div style={s.tableWrap}>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead>
            <tr>
              <th style={s.th}><input type="checkbox" className="cb"
                ref={el => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                checked={allVisibleSelected} onChange={toggleAll} /></th>
              {['Time','Tracking','Cust. Order','Recipient','Count','Warehouse','Status','Flight','Customer'].map(h=>(
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody key={listKey}>
            {loading ? (
              <tr><td colSpan={10} style={s.empty}><div style={s.emptyInner}><span style={{opacity:0.3}}>Loading...</span></div></td></tr>
            ) : filteredShipments.length === 0 ? (
              <tr><td colSpan={10} style={s.empty}>
                <div style={s.emptyInner}>
                  <div style={{fontSize:26,marginBottom:8,opacity:0.2}}>✈</div>
                  <div style={{fontWeight:600,color:'var(--text-muted)'}}>{canCountExact ? 'No shipments match the filter' : 'No assigned shipments yet'}</div>
                </div>
              </td></tr>
            ) : visibleShipments.map((sh, i)=>(
              <tr key={sh.id} className="ship-row row-animate"
                  onClick={()=>setDrawerId(sh.id)}
                  style={{borderBottom:'1px solid rgba(15,23,42,0.05)',cursor:'pointer',animationDelay:`${Math.min(i,12)*25}ms`}}>
                <td style={s.td} onClick={e=>e.stopPropagation()}>
                  <input type="checkbox" className="cb" checked={selected.includes(sh.id)} onChange={()=>toggleSelect(sh.id)} />
                </td>
                <td style={{...s.td,color:'var(--text-muted)',fontSize:11,whiteSpace:'nowrap'}}>{new Date(sh.scanned_at).toLocaleString('vi-VN')}</td>
                <td style={{...s.td,fontVariantNumeric:"tabular-nums",fontWeight:600,fontSize:11,color:'var(--text-muted)'}}>{sh.tracking_code}</td>
                <td style={{...s.td,fontVariantNumeric:"tabular-nums",fontWeight:600,color:'var(--accent)',fontSize:12}}>{sh.customer_order||'—'}</td>
                <td style={s.td}>{sh.receiver_name}</td>
                <td style={s.td} onClick={e=>e.stopPropagation()}>
                  <CanCountCell sh={sh} busy={countingIds.has(sh.id)} onCount={handleCount} onUpdate={handleUpdateCount} />
                </td>
                <td style={{...s.td,fontSize:12,color:'var(--text-secondary)',whiteSpace:'nowrap'}}>{sh.warehouse||'—'}</td>
                <td style={{...s.td,whiteSpace:'nowrap'}} onClick={e=>e.stopPropagation()}>
                  <select
                    value={sh.current_location || sh.warehouse || ''}
                    onChange={e => handleLocation(sh.id, e.target.value)}
                    style={{
                      background: sh.current_location === 'Đã chuyển lên Kho 1'
                        ? 'rgba(22,163,74,0.08)' : 'var(--bg-secondary)',
                      border: `1px solid ${sh.current_location === 'Đã chuyển lên Kho 1'
                        ? 'rgba(22,163,74,0.30)' : 'var(--border-strong)'}`,
                      borderRadius:6, padding:'5px 8px', fontSize:11,
                      color: sh.current_location === 'Đã chuyển lên Kho 1'
                        ? 'var(--color-success)' : 'var(--text-secondary)',
                       outline:'none', cursor:'pointer', maxWidth:160,
                    }}
                  >
                    <option value="Kho 1">At Warehouse 1</option>
                    <option value="Kho 2">At Warehouse 2</option>
                    <option value="Kho 3">At Warehouse 3</option>
                    <option value="Kho 4">At Warehouse 4</option>
                    <option value="Kho Surrey">At Surrey Warehouse</option>
                    <option value="ARBOURRIDGE">At ARBOURRIDGE</option>
                    <option value="Đã chuyển lên Kho 1">Transferred to Wh 1</option>
                  </select>
                </td>
                <td style={s.td}>
                  {(() => { const fb = flightBadge(sh.flight_status); return (
                    <span style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontSize:11,color:fb.color,background:fb.background,border:`1px solid ${fb.borderColor}`,padding:'3px 8px',borderRadius:5}}>
                      {sh.flight_name}
                    </span>
                  ); })()}
                </td>
                <td style={s.td}>
                  <span style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontWeight:700,fontSize:12,color:'var(--accent)'}}>{sh.customer_code}</span>
                  {sh.customer_name && <span style={{fontSize:12,color:'var(--text-secondary)',marginLeft:6}}>{sh.customer_name}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredShipments.length > PAGE_SIZE && (
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:14,padding:'0 4px',flexWrap:'wrap',gap:8}}>
          <span style={{fontSize:12,color:'var(--text-muted)'}}>
            Showing <strong style={{color:'var(--text-secondary)'}}>{(safePage-1)*PAGE_SIZE+1}–{Math.min(safePage*PAGE_SIZE,filteredShipments.length)}</strong> / {filteredShipments.length} shipments
          </span>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <button className="btn btn-secondary btn-sm" disabled={safePage<=1} onClick={()=>setPage(p=>p-1)}>
              <span className="material-symbols-outlined" style={{fontSize:14}}>chevron_left</span>
              Prev
            </button>
            <span style={{fontSize:12,color:'var(--text-secondary)',padding:'0 4px',fontVariantNumeric:'tabular-nums'}}>
              Trang {safePage} / {totalPages}
            </span>
            <button className="btn btn-secondary btn-sm" disabled={safePage>=totalPages} onClick={()=>setPage(p=>p+1)}>
              Sau
              <span className="material-symbols-outlined" style={{fontSize:14}}>chevron_right</span>
            </button>
          </div>
        </div>
      )}

      {insideModal && (
        <InsideImagesModal
          shipmentId={insideModal.id}
          tracking={insideModal.tracking}
          onClose={()=>setInsideModal(null)}
        />
      )}

      {/* Edit shipment info modal — opened from drawer's Edit button */}
      {editModal && (
        <div className="modal-overlay" onClick={()=>setEditModal(null)}>
          <div onClick={e=>e.stopPropagation()}
            style={{background:'var(--bg-secondary)',border:'1px solid var(--border-strong)',borderRadius:'var(--radius-xl)',padding:24,width:440}}>
            <div style={{fontSize:14,fontWeight:700,color:'var(--text-primary)',marginBottom:16}}>✏️ Edit shipment info</div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:10,fontWeight:700,color:'var(--text-muted)',letterSpacing:1,textTransform:'uppercase',marginBottom:6}}>Tracking Code</div>
              <input value={editModal.tracking_code} onChange={e=>setEditModal(m=>({...m,tracking_code:e.target.value}))}
                style={{width:'100%',background:'var(--bg-tertiary)',border:'1px solid var(--border)',borderRadius:8,padding:'9px 12px',fontSize:13,color:'var(--text-primary)',outline:'none',boxSizing:'border-box'}}
                onFocus={e=>{e.target.style.borderColor='var(--accent)';e.target.style.boxShadow='0 0 0 3px var(--accent-bg)';}}
                onBlur={e=>{e.target.style.borderColor='var(--border)';e.target.style.boxShadow='none';}}
              />
            </div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:10,fontWeight:700,color:'var(--text-muted)',letterSpacing:1,textTransform:'uppercase',marginBottom:6}}>Customer Order</div>
              <input value={editModal.customer_order} onChange={e=>setEditModal(m=>({...m,customer_order:e.target.value}))}
                style={{width:'100%',background:'var(--bg-tertiary)',border:'1px solid var(--border)',borderRadius:8,padding:'9px 12px',fontSize:13,color:'var(--text-primary)',outline:'none',boxSizing:'border-box'}}
                onFocus={e=>{e.target.style.borderColor='var(--accent)';e.target.style.boxShadow='0 0 0 3px var(--accent-bg)';}}
                onBlur={e=>{e.target.style.borderColor='var(--border)';e.target.style.boxShadow='none';}}
              />
            </div>
            <div style={{marginBottom:20}}>
              <div style={{fontSize:10,fontWeight:700,color:'var(--text-muted)',letterSpacing:1,textTransform:'uppercase',marginBottom:6}}>Warehouse</div>
              <select value={editModal.warehouse} onChange={e=>setEditModal(m=>({...m,warehouse:e.target.value}))}
                style={{width:'100%',background:'var(--bg-tertiary)',border:'1px solid var(--border)',borderRadius:8,padding:'9px 12px',fontSize:13,color:'var(--text-primary)',outline:'none',boxSizing:'border-box'}}>
                <option value="Kho 1">Kho 1</option>
                <option value="Kho 2">Kho 2</option>
                <option value="Kho 3">Kho 3</option>
                <option value="Kho 4">Kho 4</option>
                <option value="Kho Surrey">Kho Surrey</option>
                <option value="ARBOURRIDGE">ARBOURRIDGE</option>
              </select>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button onClick={handleSaveEdit} disabled={editLoading}
                style={{flex:1,background:'var(--bg-tertiary)',color:'var(--text-primary)',border:'1px solid var(--accent-strong)',borderRadius:8,padding:'10px',fontSize:13,fontWeight:700,cursor:'pointer'}}>
                {editLoading?'Saving...':'✓ Save'}
              </button>
              <button onClick={()=>setEditModal(null)}
                style={{flex:1,background:'var(--border)',color:'var(--text-secondary)',border:'1px solid rgba(15,23,42,0.08)',borderRadius:8,padding:'10px',fontSize:13,cursor:'pointer'}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {viewer && (
        <ImageViewer images={viewer.images} title={viewer.title} onClose={()=>setViewer(null)} />
      )}

      <ShipmentDrawer
        shipment={drawerId ? shipments.find(s => s.id === drawerId) : null}
        customers={customers}
        onClose={()=>setDrawerId(null)}
        onLocationChange={handleLocation}
        onEdit={(sh)=>setEditModal({ id: sh.id, tracking_code: sh.tracking_code || '', customer_order: sh.customer_order || '', warehouse: sh.warehouse || '' })}
        onDelete={async (id)=>{ await handleDelete(id); setDrawerId(null); }}
        onOpenLabel={(url)=>openLabelViewer(url, drawerId ? shipments.find(s=>s.id===drawerId)?.tracking_code : null)}
        onOpenInside={openInsideViewer}
        onUnassignFlight={async (sh)=>{ await handleUnassign(sh.id); setDrawerId(null); }}
        onCount={handleCount}
        onUpdateCount={handleUpdateCount}
        countingIds={countingIds}
        mode="assigned"
      />
    </div>
  );
}

const s = {
  topbar: { display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20 },
  pageLabel: { fontSize:10, fontWeight:700, color:'var(--text-muted)', letterSpacing:2, textTransform:'uppercase', marginBottom:4 },
  pageTitle: { fontSize:22, fontWeight:600, color:'var(--text-primary)', letterSpacing:-0.3 },
  badge: { background:'var(--border)', border:'1px solid rgba(15,23,42,0.08)', borderRadius:20, padding:'5px 12px', fontSize:12, color:'var(--text-secondary)' },
  kgBadge: { display:'flex', alignItems:'center', gap:6, padding:'5px 12px', background:'rgba(22,163,74,0.08)', border:'1px solid rgba(22,163,74,0.22)', borderRadius:8 },
  kgLabel: { fontSize:10, color:'var(--text-muted)', fontWeight:600, letterSpacing:1, textTransform:'uppercase' },
  kgVal: { fontVariantNumeric:"tabular-nums",fontWeight:600, fontWeight:700, fontSize:14, color:'var(--accent)' },
  canBadge: { display:'flex', alignItems:'center', gap:6, padding:'5px 12px', background:'rgba(124,58,237,0.06)', border:'1px solid rgba(124,58,237,0.22)', borderRadius:8 },
  canLabel: { fontSize:10, color:'var(--text-muted)', fontWeight:600, letterSpacing:1, textTransform:'uppercase' },
  canVal: { fontVariantNumeric:"tabular-nums",fontWeight:600, fontWeight:700, fontSize:14, color:'var(--color-purple)' },
  tableWrap: { background:'var(--bg-tertiary)', border:'1px solid rgba(15,23,42,0.07)', borderRadius:12, overflow:'hidden' },
  th: { background:'var(--bg-tertiary)', padding:'10px 12px', fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:1, textAlign:'left', borderBottom:'1px solid rgba(15,23,42,0.07)' },
  td: { padding:'10px 12px', fontSize:13, color:'var(--text-primary)' },
  empty: { padding:0 },
  emptyInner: { display:'flex', flexDirection:'column', alignItems:'center', padding:'52px 20px' },
};
